import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Loader2, Network, Search, X } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  GenealogyTree,
  GenealogyTreeSkeleton,
  type AddSlotContext,
  type GenealogyTreeNode,
} from '@mlm/ui';
import {
  getGenealogy,
  searchGenealogy,
  getTenantConfig,
  getMe,
  getSlotStanding,
  type GenealogySearchMatch,
} from '../lib/api';
import { GENEALOGY_MAX_DEPTH } from '@mlm/shared';
import { useMySlots } from '../lib/hooks/use-my-slots';
import { MemberAddSlotDialog } from '../components/member-add-slot-dialog';
import { SegmentedToggle } from '../components/segmented-toggle';
import { BinarySidePreference } from '../components/binary-side-preference';

// Every depth bump clamps to the API's cap — expanding at the boundary stops
// loading deeper instead of tripping the endpoint's zod max into a 400 (the
// "genealogy stuck at depth 15" bug, when the cap was 15).
const clampDepth = (d: number) => Math.min(d, GENEALOGY_MAX_DEPTH);

const SPONSOR_VIEW = { value: 'unilevel', label: 'Sponsor' } as const;
const BINARY_VIEW = { value: 'binary', label: 'Binary' } as const;
type GenealogyView = 'unilevel' | 'binary';

export function GenealogyPage() {
  const { activeSlot } = useMySlots();
  // null = the member hasn't touched the toggle, so the tenant default wins.
  // Storing the CHOICE rather than the resolved view is what keeps a manual
  // switch from being clobbered when /me or standing refetch a tick later.
  const [picked, setPicked] = useState<GenealogyView | null>(null);
  // Lazy depth: root + immediate level first; expanding the lowest node loads
  // the next level (see onLoadChildren).
  const [depth, setDepth] = useState(1);
  const [addCtx, setAddCtx] = useState<AddSlotContext | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [target, setTarget] = useState<{ slotId: string; path: string[] } | null>(null);

  // Already in cache — the layout fetches ['me'] on every authed page.
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe, staleTime: 60_000 });
  // standing.binary is null iff the slot has no binary_tree row, i.e. unplaced.
  // (A true ROOT slot has a row with a null parent, so row absence is the test.)
  const standingQ = useQuery({
    queryKey: ['standing', activeSlot?.id],
    queryFn: () => getSlotStanding(activeSlot!.id),
    enabled: !!activeSlot,
  });

  // Both stay `undefined` until their query settles, so we render nothing
  // rather than paint one tree and snap to the other. The `!!meQ.data &&` guard
  // matters: a bare .includes() on an absent list reads as "enabled", which
  // would flash Binary on a binary-OFF tenant. This fails closed to Sponsor.
  const binaryOn = meQ.isPending
    ? undefined
    : !!meQ.data && !(meQ.data.tenantPolicy?.disabledPlanCodes ?? []).includes('binary');
  const placed = standingQ.isPending ? undefined : !!standingQ.data?.binary;

  // Binary is FIRST in the pill order whenever the plan is on, but only OPENS
  // when the member is actually placed — an unplaced member gets Sponsor, which
  // has something to show. Plan off = no Binary tab at all.
  const views = binaryOn ? [BINARY_VIEW, SPONSOR_VIEW] : [SPONSOR_VIEW];
  const view: GenealogyView | undefined =
    binaryOn === undefined || placed === undefined
      ? undefined
      : (picked ?? (binaryOn && placed ? 'binary' : 'unilevel'));

  const q = useQuery({
    queryKey: ['genealogy', activeSlot?.id, view, depth],
    queryFn: () => getGenealogy({ slotId: activeSlot!.id, view: view!, depth }),
    enabled: !!activeSlot && !!view,
    // `depth` is part of the key, so expanding a node (which bumps depth) would
    // otherwise spin up a fresh query with no cached data — the tree blanks to a
    // skeleton / collapses to the root, then snaps back when data lands. Keep the
    // current tree on screen while the next level loads so caret toggles and
    // placements update smoothly instead of flickering.
    placeholderData: keepPreviousData,
  });

  const tenantQ = useQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig });
  // Default on — only an explicit false hides the "Add New Slot" affordance.
  const enableSlotActivation = tenantQ.data?.genealogyActivateSlot !== false;
  // When Auto Place is on, new slots auto-place via spillover, so the binary
  // tree's manual "Activate New Slot" (place-a-seat) placeholders are hidden.
  const autoPlace = tenantQ.data?.autoPlaceSlots === true;

  const trimmed = searchTerm.trim();
  const searchQ = useQuery({
    queryKey: ['genealogy-search', activeSlot?.id, view, trimmed],
    queryFn: () => searchGenealogy({ slotId: activeSlot!.id, view: view!, q: trimmed }),
    enabled: !!activeSlot && !!view && trimmed.length >= 2,
  });

  function jumpTo(m: GenealogySearchMatch) {
    // Make sure the match is within the loaded depth, then expand + highlight.
    setDepth((d) => clampDepth(Math.max(d, m.depth)));
    setTarget({ slotId: m.slotId, path: m.pathSlotIds });
    setSearchTerm('');
  }

  // `view` is undefined until /me and standing settle; narrowing it here keeps
  // the whole JSX body typed as a concrete view.
  if (!activeSlot || !view) return null;

  const rootNode: GenealogyTreeNode = {
    slotId: activeSlot.id,
    slotCode: activeSlot.slotCode,
    userFullName: null,
    userEmail: null,
    depth: 0,
    parentSlotId: null,
    membershipCode: null,
    membershipName: null,
    packageAmount: null,
    descendantCount: q.data ? Math.max(0, q.data.nodes.length - 1) : 0,
    childCount: 0,
  };

  const nodes: GenealogyTreeNode[] = q.data?.nodes ?? [rootNode];
  const currency = q.data?.currency ?? 'PHP';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-h1">
            <Network className="h-6 w-6 text-muted-foreground" /> Genealogy
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Root: <span className="font-mono">{activeSlot.slotCode}</span>
          </p>
        </div>
        <SegmentedToggle
          ariaLabel="Genealogy view"
          options={views}
          value={view}
          onChange={(v) => {
            setPicked(v);
            setTarget(null);
          }}
        />
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-8 pr-8"
          placeholder="Search your affiliates (name, slot code, or referral code)…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            onClick={() => setSearchTerm('')}
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {trimmed.length >= 2 && (
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
            {searchQ.isLoading ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">Searching…</p>
            ) : !searchQ.data?.matches.length ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">No matching affiliate.</p>
            ) : (
              <ul className="max-h-64 overflow-auto">
                {searchQ.data.matches.map((m) => (
                  <li key={m.slotId}>
                    <button
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-accent"
                      onClick={() => jumpTo(m)}
                    >
                      <span className="min-w-0">
                        <span className="font-mono text-xs">{m.slotCode}</span>
                        {m.userFullName && (
                          <span className="ml-2 truncate text-muted-foreground">
                            {m.userFullName}
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        depth {m.depth}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {view === 'binary' ? <BinarySidePreference slotId={activeSlot.id} /> : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {view === 'unilevel' ? 'Sponsor network' : 'Binary network'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {view === 'binary' && !placed ? (
            // No binary_tree row yet. The tree would render as "Nobody under you
            // yet", which is wrong — they have no seat at all, not an empty
            // downline. Only reachable by clicking the pill, since an unplaced
            // member opens on Sponsor.
            <p className="py-8 text-center text-sm text-muted-foreground">
              Your slot isn&apos;t placed in the binary tree yet. Once your sponsor places you, your
              binary affiliates will show up here.
            </p>
          ) : q.isLoading ? (
            <GenealogyTreeSkeleton />
          ) : (
            <GenealogyTree
              nodes={nodes}
              rootSlotId={activeSlot.id}
              view={view}
              currency={currency}
              onAddSlot={setAddCtx}
              enableSlotActivation={enableSlotActivation}
              nodeFields={view === 'binary' ? tenantQ.data?.binaryGenealogyNodeFields : undefined}
              binaryPlacementReadOnly={autoPlace && view === 'binary'}
              expandPath={target?.path}
              highlightSlotId={target?.slotId}
              defaultExpandedDepth={1}
              onLoadChildren={(n) => setDepth((d) => clampDepth(Math.max(d, n.depth + 1)))}
              emptyMessage="Nobody under you yet. Share your replicated link to grow your affiliate network."
            />
          )}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              Drag to pan, scroll+Ctrl (or pinch) to zoom; expand a node to load the next level.
            </span>
            {q.isFetching && <Loader2 className="h-4 w-4 animate-spin" />}
          </div>
        </CardContent>
      </Card>

      <MemberAddSlotDialog
        ctx={addCtx}
        view={view}
        anchorSlotId={activeSlot.id}
        allowRedeem={enableSlotActivation}
        onClose={() => setAddCtx(null)}
        onPlaced={() => {
          // The placed slot sits one level below the seat we filled, which can
          // be deeper than the current fetch depth (you can place into an empty
          // seat shown below the deepest loaded node). Extend the depth so the
          // refetch includes it — otherwise it only appears after a reload or a
          // caret toggle (which is what bumps depth via onLoadChildren).
          const parentDepth = nodes.find((n) => n.slotId === addCtx?.parentSlotId)?.depth ?? 0;
          setDepth((d) => clampDepth(Math.max(d, parentDepth + 1)));
        }}
      />
    </div>
  );
}
